import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import TextFieldInput from "../../forms/TextFieldInput";
import SelectFieldInput from "../../forms/SelectFieldInput";

import { useNavigate, useParams } from "react-router-dom";
import { fetchSecurityInfoById, updateSecurity } from "../../../services/Axios";

export default function EditSecurity() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { control, handleSubmit, reset, watch } = useForm();
  const [error, setError] = useState(null);

  React.useEffect(() => {
    fetchSecurityInfoById(id)
      .then((response) => {
        reset(response);
      })
      .catch((error) => {
        console.error("Error fetching security:", error);
        navigate("/SecurityMaster/");
      });
  }, [id, reset, navigate]);

  const onSubmit = (data) => {
    updateSecurity(id, data)
      .then((response) => {
        navigate("/SecurityMaster/");
      })
      .catch((error) => {
        if (error.response?.data) {
          // Handle different error response formats
          let errorMessage = "";

          if (typeof error.response.data === "string") {
            // If the error is a simple string
            errorMessage = error.response.data;
          } else if (typeof error.response.data === "object") {
            // If the error is an object
            errorMessage = Object.entries(error.response.data)
              ?.map(([field, messages]) => {
                // Handle both array and string messages
                const messageText = Array.isArray(messages)
                  ? messages.join(", ")
                  : messages;
                return `${field}: ${messageText}`;
              })
              .join("\n");
          }

          setError(errorMessage || "An error occurred");
        } else {
          setError("An unexpected error occurred");
        }
        console.error("Error details:", error.response?.data); // Add this for debugging
      });
  };

  return (
    <>
      <Box
        sx={{
          p: 1.5,
          maxWidth: "1200px",
          mx: "auto",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.02
          )} 0%, ${alpha(
            theme.palette.secondary?.main || theme.palette.primary.main,
            0.02
          )} 100%)`,
          minHeight: "100vh",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: theme.palette.background.paper,
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              borderBottom: `2px solid ${alpha(
                theme.palette.primary.main,
                0.2
              )}`,
              p: 1.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                letterSpacing: "0.3px",
                fontSize: "1.2rem",
                color: theme.palette.text.primary,
              }}
            >
              Edit Security
            </Typography>
          </Box>

          {/* Form Content */}
          <Box sx={{ p: 2, background: theme.palette.background.paper }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Submit Button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  mb: 2,
                }}
              >
                <Button
                  variant="contained"
                  type="submit"
                  size="medium"
                  sx={{
                    minWidth: "180px",
                    backgroundColor: theme.palette.success?.main || "#2e7d32",
                    color: "#ffffff",
                    fontWeight: 600,
                    textTransform: "none",
                    letterSpacing: "0.5px",
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    boxShadow: `0 2px 8px ${alpha(
                      theme.palette.success?.main || "#2e7d32",
                      0.3
                    )}`,
                    "&:hover": {
                      backgroundColor: theme.palette.success?.dark || "#1b5e20",
                      boxShadow: `0 4px 12px ${alpha(
                        theme.palette.success?.main || "#2e7d32",
                        0.4
                      )}`,
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Update Security
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {/* Row 1 - Basic Information */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      mt: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 18,
                        borderRadius: 1,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontSize: "0.9rem",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Basic Information
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5, mt: 0.5 }} />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="SYMBOL"
                    label="Symbol"
                    placeholder="Enter security symbol"
                    required={true}
                    rules={{ required: "Symbol is required" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="name"
                    label="Security Name"
                    placeholder="Enter security name"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="currency"
                    label="Currency"
                    placeholder="Enter currency code"
                    required={true}
                    rules={{ required: "Currency is required" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectFieldInput
                    control={control}
                    name="pricing_source"
                    label="Pricing Source"
                    required={true}
                    rules={{ required: "Pricing Source is required" }}
                    options={[
                      { value: "Bloomberg", label: "Bloomberg" },
                      { value: "Spreadsheet", label: "Spreadsheet" },
                      { value: "Static", label: "Static" },
                      { value: "Manual", label: "Manual" },
                      { value: "Cross_border_EU", label: "Cross Border EU" },
                      { value: "Formula", label: "Formula" },
                      { value: "Convert_Nukes", label: "Convert Nukes" },
                    ]}
                  />
                </Grid>

                {/* Row 2 - Security Types and Multipliers */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      mt: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 18,
                        borderRadius: 1,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontSize: "0.9rem",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Security Configuration
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5, mt: 0.5 }} />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectFieldInput
                    control={control}
                    name="security_type"
                    label="Security Type"
                    required={true}
                    rules={{ required: "Security Type is required" }}
                    options={[
                      { value: "CASH", label: "CASH" },
                      { value: "BOND-CORP", label: "BOND-CORP" },
                      { value: "BOND-GOVT", label: "BOND-GOVT" },
                      { value: "BOND-CONV", label: "BOND-CONV" },
                      { value: "EQ-DOMESTIC", label: "EQ-DOMESTIC" },
                      { value: "EQ-CL", label: "EQ-CL" },
                      { value: "EQ-PT", label: "EQ-PT" },
                      { value: "EQ-DOM-SWAP", label: "EQ-DOM-SWAP" },
                      { value: "IDX-SWAP", label: "IDX-SWAP" },
                      { value: "EQ-INTL-SWAP", label: "EQ-INTL-SWAP" },
                      { value: "TRS-BANKDEBT", label: "TRS-BANKDEBT" },
                      { value: "BANKDEBT", label: "BANKDEBT" },
                      { value: "EQ-INTL", label: "EQ-INTL" },
                      { value: "EQ-INTL-SWP-MNR", label: "EQ-INTL-SWP-MNR" },
                      { value: "EQ-MINOR", label: "EQ-MINOR" },
                      { value: "BOND-CVT-SWAP", label: "BOND-CVT-SWAP" },
                      { value: "WARRANT", label: "WARRANT" },
                      { value: "RIGHT", label: "RIGHT" },
                      { value: "", label: "Undefined" },
                    ]}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectFieldInput
                    control={control}
                    name="security_type2"
                    label="Security Type 2"
                    required={true}
                    rules={{ required: "Security Type 2 is required" }}
                    options={[
                      { value: "CASH", label: "CASH" },
                      { value: "Bond", label: "Bond" },
                      { value: "Convertible_Bond", label: "Convertible Bond" },
                      { value: "Option", label: "Option" },
                      { value: "Equity", label: "Equity" },
                    ]}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="price_multiplier"
                    label="Price Multiplier"
                    placeholder="Enter price multiplier"
                    type="number"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="position_multiplier"
                    label="Position Multiplier"
                    placeholder="Enter position multiplier"
                    type="number"
                  />
                </Grid>

                {/* Row 3 - Pricing Information */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      mt: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 18,
                        borderRadius: 1,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontSize: "0.9rem",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Pricing Information
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5, mt: 0.5 }} />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="last_closing_price"
                    label="Last Closing Price"
                    placeholder="Enter last closing price"
                    type="number"
                    required={watch("pricing_source") === "Static"}
                    rules={
                      watch("pricing_source") === "Static"
                        ? {
                            required:
                              "Last closing price is required for Static pricing",
                          }
                        : {}
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="manual_price_input"
                    label="Manual Price Input"
                    placeholder="Enter manual price"
                    required={["Manual", "Formula"].includes(
                      watch("pricing_source")
                    )}
                    rules={
                      ["Manual", "Formula"].includes(watch("pricing_source"))
                        ? {
                            required:
                              "Manual price input is required for Manual/Formula pricing",
                          }
                        : {}
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="Ticker_Exchange_Code"
                    label="Ticker Exchange Code"
                    placeholder="Enter exchange code"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextFieldInput
                    control={control}
                    name="BB_Ticker_Code"
                    label="Bloomberg Ticker"
                    placeholder="Enter Bloomberg ticker"
                  />
                </Grid>

                {/* Row 4 - Exclusion Settings */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      mt: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 18,
                        borderRadius: 1,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontSize: "0.9rem",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Exclusion Settings
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5, mt: 0.5 }} />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectFieldInput
                    control={control}
                    name="exclude_from_real_time_pnl_today"
                    label="Exclude RT PNL Today"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectFieldInput
                    control={control}
                    name="exclude_from_real_time_pnl_forever"
                    label="Exclude RT PNL Forever"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectFieldInput
                    control={control}
                    name="exclude_from_risk_calc_today"
                    label="Exclude Risk Today"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectFieldInput
                    control={control}
                    name="exclude_from_risk_calc_forever"
                    label="Exclude Risk Forever"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                  />
                </Grid>

                {/* Row 5 - Security Attributes */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      mt: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 18,
                        borderRadius: 1,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontSize: "0.9rem",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Additional Attributes
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5, mt: 0.5 }} />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.03),
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.1
                      )}`,
                    }}
                  >
                    <TextFieldInput
                      control={control}
                      name="security_attributes"
                      label="Security Attributes (JSON)"
                      placeholder="Enter security attributes in JSON format"
                      multiline
                      rows={3}
                    />
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setError(null)} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
